import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Notification, NotificationData } from '../../models/notification.model';
import { UserToken } from '../../models/user.model';
import { ApiDigitalAdminNotification } from '../../services/admin/notification.service';
import { ApiDigitalAuthInvitation } from '../../services/auth/invitation.service';
import { AppServiceToken } from '../../models/app.service.model';
import { CompanyToken } from '../../models/company.model';
import { Invitation } from '../../models/invitation.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { GlobalSwal } from '../../styles/swal.style';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { getError, getMessage } from '../../languages/translations/response';
import { toastError, toastSuccess } from '../../utils/toast.util';
import { uploadTooltip } from '../../utils/tooltip.util';
import { modalShow } from '../../utils/modal.util';

import AppLoading from '../Loading';
import AppModalInvitationView from '../modal/ModalInvitationView';
import AppModalNotificationView from '../modal/ModalNotificationView';
import EmptyState from '../element/EmptyState';
import imgMessages from '../../assets/themes/images/new-message.svg';
import imgSettings from '../../assets/themes/images/settings.svg';

export interface AppDropdownNotificationsProps {
  userLoggedIn: UserToken | null,
  appServiceForUser: AppServiceToken | null,
  companyForUser: CompanyToken | null,
  notificationsUnread: number,
  loadNotificationsUnread: Function,
  reloadSession: Function
};

let errorResponse: Error, messageResponse: Message, readResponse: NotificationData, unreadResponse: NotificationData, archivedResponse: NotificationData, invitationsResponse: Invitation[];

const AppDropdownNotifications: React.FunctionComponent<AppDropdownNotificationsProps> = ({userLoggedIn, appServiceForUser, companyForUser, notificationsUnread, loadNotificationsUnread, reloadSession}) => {
  const {lang} = StateLanguage()

  const [notification, setNotification] = useState<Notification | null>(null)
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [unread, setUnread] = useState<Notification[] | null>(null)
  const [read, setRead] = useState<Notification[] | null>(null)
  const [archived, setArchived] = useState<Notification[] | null>(null)
  const [invitations, setInvitations] = useState<Invitation[] | null>(null)

  const loadInvitations = async (api_key: string) => {
    await ApiDigitalAuthInvitation.getMyInvitations(api_key).then( (response) => {
      if (response.status === 200) {
        invitationsResponse = response.data
        setInvitations(invitationsResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadUnread = async () => {
    await ApiDigitalAdminNotification.getNotifications(1, 100, 'unread', '').then( (response) => {
      if (response.status === 200) {
        unreadResponse = response.data
        setUnread(unreadResponse.items)
        uploadTooltip()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadRead = async () => {
    await ApiDigitalAdminNotification.getNotifications(1, 100, 'read', '').then( (response) => {
      if (response.status === 200) {
        readResponse = response.data
        setRead(readResponse.items)
        uploadTooltip()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadArchived = async () => {
    await ApiDigitalAdminNotification.getNotifications(1, 100, 'archived', '').then( (response) => {
      if (response.status === 200) {
        archivedResponse = response.data
        setArchived(archivedResponse.items)
        uploadTooltip()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    }) 
  }

  const executeSelectInvitation = (item: Invitation) => {
    setInvitation(item)
    modalShow('modal-invitation-view')
  }

  const executeSelectNotification = async (item: Notification) => {
    setNotification(item)
    modalShow('modal-notification-view')
  }

  const executeArchiveNotification = async (item: string) => {
    await ApiDigitalAdminNotification.putNotificationArchive(item).then( (response) => {
      if (response.status === 200) {
        messageResponse = response.data
        toastSuccess(getMessage(messageResponse.message, lang.code))
        loadNotificationsUnread()
      } else {
        errorResponse = response.data
        toastError(getError(errorResponse.code, lang.code, errorResponse.message))
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const executeViewAllNotification = async () => {
    await ApiDigitalAdminNotification.putNotificationReadAll().then( (response) => {
      if (response.status === 200) {
        messageResponse = response.data
        toastSuccess(getMessage(messageResponse.message.replace(/^\d+\s+/, '').replace(/^./, x => x.toUpperCase()), lang.code))
        loadNotificationsUnread()
      } else {
        errorResponse = response.data
        toastError(getError(errorResponse.code, lang.code, errorResponse.message))
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  function timeElapsed(date: string): string {
    let dateNotification: Date = new Date(date)
    let dateCurrent: Date = new Date()
    let momentNotification: Moment = moment(dateNotification)
    let momentCurrent: Moment = moment(dateCurrent)
    let minutes: number = momentCurrent.diff(momentNotification, 'minute')
    let hours: number = momentCurrent.diff(momentNotification, 'hour')
    let days: number = momentCurrent.diff(momentNotification, 'day')
    let months: number = momentCurrent.diff(momentNotification, 'month')
    let years: number = momentCurrent.diff(momentNotification, 'year')

    if (years > 0) {
      return `${years} ${(years === 1) ? lang.labels.yearl : lang.labels.years}`
    } else {
      if (months > 0) {
        return `${months} ${(months === 1) ? lang.labels.month : lang.labels.months}`
      } else {
        if (days > 0) {
          return `${days} ${(days === 1) ? lang.labels.day : lang.labels.days}`
        } else {
          if (hours > 0) {
            return `${hours} ${(hours === 1) ? lang.labels.hour : lang.labels.hours}`
          } else {
            if (minutes > 0) {
              return `${minutes} ${(minutes === 1) ? lang.labels.minute : lang.labels.minutes}`
            } else {
              return lang.labels.justNow
            }
          }
        }
      }
    }
  }

  useEffect(() => {
    if (companyForUser && companyForUser.id) {
      loadUnread()
      loadRead()
      loadArchived()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsUnread, companyForUser])

  useEffect(() => {
    if (appServiceForUser) {
      loadInvitations(appServiceForUser.api_key)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  return (
    <>
      <div id="dropdown-notifications" className="menu menu-sub menu-sub-dropdown menu-column w-350px" data-kt-menu="true">
        <div id="background-dropdown-header" className="d-flex flex-column bgi-no-repeat rounded-top">
          <h3 className="text-white p-5 pb-3 m-0">
            {lang.labels.notifications}
            <span className="fs-8 opacity-75 ps-3">{notificationsUnread} {notificationsUnread === 1 ? `${lang.labels.report}` : `${lang.labels.reports}`}</span>
          </h3>
          <ul className="nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-bold px-3">
            <li className="nav-item">
              <a href="#notifications" className="nav-link text-white opacity-75 opacity-state-100 active" data-bs-toggle="tab">{lang.labels.records}</a>
            </li>
            <li className="nav-item">
              <a href="#invitations" className="nav-link text-white opacity-75 opacity-state-100" data-bs-toggle="tab">{lang.labels.invitations}</a>
            </li>
            <li className="nav-item">
              <a href="#archived" className="nav-link text-white opacity-75 opacity-state-100" data-bs-toggle="tab">{lang.labels.archived}</a>
            </li>
            <li className="nav-item">
              <a href="#other" className="nav-link text-white opacity-75 opacity-state-100" data-bs-toggle="tab">{lang.labels.other}</a>
            </li>
          </ul>
        </div>
        <div className="tab-content h-100">
          <div id="notifications" className="tab-pane fade h-100 show active" role="tabpanel">
            <div className="position-relative h-100 py-2 px-3">
              <div className="scroll-y mh-400px">
                { unread && read
                  ?
                  <>
                    { unread.length === 0 && read.length === 0
                      ?
                      <EmptyState illustration={imgMessages} illustrationAlt="" title={lang.labels.noNotifications} description={lang.labels.thereAreNoNotifications} size="sm" />
                      :
                      <>
                        { unread.map (( (item, index) => {
                          return (
                            <div key={index} className="d-flex flex-stack py-3">
                              <div className="d-flex align-items-center me-3 overflow-hidden">
                                <div className="symbol symbol-35px me-2 flex-shrink-0">
                                  <span className="symbol-label bg-light-info">
                                    <span className="svg-icon svg-icon-2 svg-icon-primary">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path opacity="0.3" d="M21 19H3C2.4 19 2 18.6 2 18V6C2 5.4 2.4 5 3 5H21C21.6 5 22 5.4 22 6V18C22 18.6 21.6 19 21 19Z" fill="black"/>
                                        <path d="M21 5H2.99999C2.69999 5 2.49999 5.10005 2.29999 5.30005L11.2 13.3C11.7 13.7 12.4 13.7 12.8 13.3L21.7 5.30005C21.5 5.10005 21.3 5 21 5Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </span>
                                </div>
                                <div className="row overflow-hidden"> 
                                  <span className="text-hover-primary fw-bolder cursor-pointer" onClick={() => executeSelectNotification(item)}>{item.title}</span>
                                  <small className="text-muted text-truncate">{item.message}</small>
                                </div>
                              </div>
                              <span className="badge badge-light text-lowercase fs-9 me-2 flex-shrink-0">{timeElapsed(item.created_at)}</span>
                              <button className="btn btn-sm btn-icon btn-flex btn-bg-transparent btn-active-color-secondary h-30px w-30px" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="right" data-bs-original-title={lang.labels.archive} onClick={() => executeArchiveNotification(item.id)}>
                                <span className="svg-icon svg-icon-primary svg-icon-2">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M10 4H21C21.6 4 22 4.4 22 5V7H10V4Z" fill="black"/>
                                    <path d="M9.2 3H3C2.4 3 2 3.4 2 4V19C2 19.6 2.4 20 3 20H21C21.6 20 22 19.6 22 19V7C22 6.4 21.6 6 21 6H12L10.4 3.60001C10.2 3.20001 9.7 3 9.2 3Z" fill="black"/>
                                  </svg>
                                </span>
                              </button>
                            </div>
                          )
                        }))}
                        { read.map (( (item, index) => {
                          return (
                            <div key={index} className="d-flex flex-stack py-3">
                              <div className="d-flex align-items-center me-3 overflow-hidden">
                                <div className="symbol symbol-35px me-2 flex-shrink-0">
                                  <span className="symbol-label bg-light-secondary">
                                    <span className="svg-icon svg-icon-2 svg-icon-primary">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 8.725C6 8.125 6.4 7.725 7 7.725H14L18 11.725V12.925L22 9.725L12.6 2.225C12.2 1.925 11.7 1.925 11.4 2.225L2 9.725L6 12.925V8.725Z" fill="black"/>
                                        <path opacity="0.3" d="M22 9.72498V20.725C22 21.325 21.6 21.725 21 21.725H3C2.4 21.725 2 21.325 2 20.725V9.72498L11.4 17.225C11.8 17.525 12.3 17.525 12.6 17.225L22 9.72498ZM15 11.725H18L14 7.72498V10.725C14 11.325 14.4 11.725 15 11.725Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </span>
                                </div>
                                <div className="row overflow-hidden"> 
                                  <span className="text-hover-primary fw-bolder cursor-pointer fs-7" onClick={() => executeSelectNotification(item)}>{item.title}</span>
                                  <small className="text-muted text-truncate fs-8">{item.message}</small>
                                </div>
                              </div>
                              <span className="badge badge-light text-lowercase fs-9 me-2 flex-shrink-0">{timeElapsed(item.created_at)}</span>
                            </div>
                          )
                        }))}
                      </>
                    }
                  </>
                  :
                  <AppLoading></AppLoading>
                }
              </div>
              { unread && unread.length > 1 &&
                <button className="btn btn-icon btn-circle btn-primary position-absolute bottom-0 end-0 me-3 mb-3" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.viewAll} onClick={executeViewAllNotification}>
                  <span className="svg-icon svg-icon-1">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 8.725C6 8.125 6.4 7.725 7 7.725H14L18 11.725V12.925L22 9.725L12.6 2.225C12.2 1.925 11.7 1.925 11.4 2.225L2 9.725L6 12.925V8.725Z" fill="black"/>
                      <path opacity="0.3" d="M22 9.72498V20.725C22 21.325 21.6 21.725 21 21.725H3C2.4 21.725 2 21.325 2 20.725V9.72498L11.4 17.225C11.8 17.525 12.3 17.525 12.6 17.225L22 9.72498ZM15 11.725H18L14 7.72498V10.725C14 11.325 14.4 11.725 15 11.725Z" fill="black"/>
                    </svg>
                  </span>
                </button>
              }
            </div>
          </div>
          <div id="invitations" className="tab-pane fade h-100" role="tabpanel">
            <div className="scroll-y mh-400px py-2 px-3">
              { invitations
                ?
                <>
                  { invitations.length === 0
                    ?
                    <EmptyState illustration={imgMessages} illustrationAlt="" title={lang.labels.noNotifications} description={lang.labels.thereAreNoNotifications} size="sm" />
                    :
                    <>
                      { invitations.map (( (item, index) => {
                        return (
                          <div key={index} className="d-flex flex-stack py-3">
                            <div className="d-flex align-items-center me-3 overflow-hidden">
                              <div className="symbol symbol-35px me-2 flex-shrink-0">
                                { item.status === 'pending' &&
                                  <span className="symbol-label bg-light-info">
                                    <span className="svg-icon svg-icon-1 svg-icon-primary">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path opacity="0.3" d="M20.5543 4.37824L12.1798 2.02473C12.0626 1.99176 11.9376 1.99176 11.8203 2.02473L3.44572 4.37824C3.18118 4.45258 3 4.6807 3 4.93945V13.569C3 14.6914 3.48509 15.8404 4.4417 16.984C5.17231 17.8575 6.18314 18.7345 7.446 19.5909C9.56752 21.0295 11.6566 21.912 11.7445 21.9488C11.8258 21.9829 11.9129 22 12.0001 22C12.0872 22 12.1744 21.983 12.2557 21.9488C12.3435 21.912 14.4326 21.0295 16.5541 19.5909C17.8169 18.7345 18.8277 17.8575 19.5584 16.984C20.515 15.8404 21 14.6914 21 13.569V4.93945C21 4.6807 20.8189 4.45258 20.5543 4.37824Z" fill="black"/>
                                        <path d="M14.854 11.321C14.7568 11.2282 14.6388 11.1818 14.4998 11.1818H14.3333V10.2272C14.3333 9.61741 14.1041 9.09378 13.6458 8.65628C13.1875 8.21876 12.639 8 12 8C11.361 8 10.8124 8.21876 10.3541 8.65626C9.89574 9.09378 9.66663 9.61739 9.66663 10.2272V11.1818H9.49999C9.36115 11.1818 9.24306 11.2282 9.14583 11.321C9.0486 11.4138 9 11.5265 9 11.6591V14.5227C9 14.6553 9.04862 14.768 9.14583 14.8609C9.24306 14.9536 9.36115 15 9.49999 15H14.5C14.6389 15 14.7569 14.9536 14.8542 14.8609C14.9513 14.768 15 14.6553 15 14.5227V11.6591C15.0001 11.5265 14.9513 11.4138 14.854 11.321ZM13.3333 11.1818H10.6666V10.2272C10.6666 9.87594 10.7969 9.57597 11.0573 9.32743C11.3177 9.07886 11.6319 8.9546 12 8.9546C12.3681 8.9546 12.6823 9.07884 12.9427 9.32743C13.2031 9.57595 13.3333 9.87594 13.3333 10.2272V11.1818Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </span>
                                }
                                { item.status === 'accept' &&
                                  <span className="symbol-label bg-light-secondary">
                                    <span className="svg-icon svg-icon-1 svg-icon-secondary">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path opacity="0.3" d="M20.5543 4.37824L12.1798 2.02473C12.0626 1.99176 11.9376 1.99176 11.8203 2.02473L3.44572 4.37824C3.18118 4.45258 3 4.6807 3 4.93945V13.569C3 14.6914 3.48509 15.8404 4.4417 16.984C5.17231 17.8575 6.18314 18.7345 7.446 19.5909C9.56752 21.0295 11.6566 21.912 11.7445 21.9488C11.8258 21.9829 11.9129 22 12.0001 22C12.0872 22 12.1744 21.983 12.2557 21.9488C12.3435 21.912 14.4326 21.0295 16.5541 19.5909C17.8169 18.7345 18.8277 17.8575 19.5584 16.984C20.515 15.8404 21 14.6914 21 13.569V4.93945C21 4.6807 20.8189 4.45258 20.5543 4.37824Z" fill="black"/>
                                        <path d="M10.5606 11.3042L9.57283 10.3018C9.28174 10.0065 8.80522 10.0065 8.51412 10.3018C8.22897 10.5912 8.22897 11.0559 8.51412 11.3452L10.4182 13.2773C10.8099 13.6747 11.451 13.6747 11.8427 13.2773L15.4859 9.58051C15.771 9.29117 15.771 8.82648 15.4859 8.53714C15.1948 8.24176 14.7183 8.24176 14.4272 8.53714L11.7002 11.3042C11.3869 11.6221 10.874 11.6221 10.5606 11.3042Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </span>
                                }
                                { item.status === 'reject' &&
                                  <span className="symbol-label bg-light-secondary">
                                    <span className="svg-icon svg-icon-1 svg-icon-secondary">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path opacity="0.3" d="M20.5543 4.37824L12.1798 2.02473C12.0626 1.99176 11.9376 1.99176 11.8203 2.02473L3.44572 4.37824C3.18118 4.45258 3 4.6807 3 4.93945V13.569C3 14.6914 3.48509 15.8404 4.4417 16.984C5.17231 17.8575 6.18314 18.7345 7.446 19.5909C9.56752 21.0295 11.6566 21.912 11.7445 21.9488C11.8258 21.9829 11.9129 22 12.0001 22C12.0872 22 12.1744 21.983 12.2557 21.9488C12.3435 21.912 14.4326 21.0295 16.5541 19.5909C17.8169 18.7345 18.8277 17.8575 19.5584 16.984C20.515 15.8404 21 14.6914 21 13.569V4.93945C21 4.6807 20.8189 4.45258 20.5543 4.37824Z" fill="black"/>
                                        <rect x="9" y="13.0283" width="7.3536" height="1.2256" rx="0.6128" transform="rotate(-45 9 13.0283)" fill="black"/>
                                        <rect x="9.86664" y="7.93359" width="7.3536" height="1.2256" rx="0.6128" transform="rotate(45 9.86664 7.93359)" fill="black"/>
                                      </svg>
                                    </span>
                                  </span>
                                }
                              </div>
                              <div className="row overflow-hidden"> 
                                <span className={`text-hover-primary fw-bolder cursor-pointer ${item.status !== "pending" && "fs-7"}`} onClick={() => executeSelectInvitation(item)}>{item.company_id?.name ?? lang.labels.company}</span>
                                <small className={`text-muted text-truncate ${item.status !== "pending" && "fs-8"}`}>{lang.labels.accessToCompany}</small>
                              </div>
                            </div>
                            <span className="badge badge-light text-lowercase fs-9 me-2 flex-shrink-0">{timeElapsed(item.created_at)}</span>
                          </div>
                        )
                      }))}
                    </>
                  }
                </>
                :
                <AppLoading></AppLoading>
              }
            </div>
          </div>
          <div id="archived" className="tab-pane fade h-100" role="tabpanel">
            <div className="scroll-y mh-400px py-2 px-3">
              { archived
                ?
                <>
                  { archived.length === 0
                    ?
                    <EmptyState illustration={imgMessages} illustrationAlt="" title={lang.labels.noNotifications} description={lang.labels.noNotificationsSeenYet} size="sm" />
                    :
                    <>
                      { archived.map (( (item, index) => {
                        return (
                        <div key={index} className="d-flex flex-stack py-3">
                          <div className="d-flex align-items-center me-3 overflow-hidden">
                            <span className="w-80px badge badge-light-info me-3 fs-8 text-uppercase flex-shrink-0">{item.title}</span>
                            <span className="text-hover-primary text-line-2 fw-bold fs-7 cursor-pointer" onClick={() => executeSelectNotification(item)}>
                              {item.message}
                            </span>
                          </div>
                          <span className="badge badge-light text-lowercase fs-9 flex-shrink-0">{timeElapsed(item.created_at)}</span>
                        </div>
                        )
                      }))}
                    </>
                  }
                </>
                :
                <AppLoading></AppLoading>
              }
            </div>
          </div>
          <div id="other" className="tab-pane fade h-100" role="tabpanel">
            <EmptyState illustration={imgSettings} illustrationAlt="" title={lang.labels.contactUs} description={lang.labels.ifYouHaveAnyQuestionsContactUs} size="sm" action={<a href="https://digitaladmin.ch" className="btn btn-sm btn-primary">{lang.labels.digitalAdmin}</a>} />
          </div>
        </div>
      </div>
      <AppModalNotificationView userLoggedIn={userLoggedIn} notification={notification} loadNotificationsUnread={loadNotificationsUnread}></AppModalNotificationView>
      <AppModalInvitationView appServiceForUser={appServiceForUser} companyForUser={companyForUser} invitation={invitation} loadInvitations={loadInvitations} reloadSession={reloadSession}></AppModalInvitationView>
    </>
  )
}

export default AppDropdownNotifications;
